import {
  Badge,
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import EuroIcon from '@mui/icons-material/Euro';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

interface NavItemLink {
  label: string;
  icon: React.ReactNode;
  path: string;
}
interface NavItemGroup {
  label: string;
  icon: React.ReactNode;
  children: { label: string; path: string }[];
}
type NavItem = NavItemLink | NavItemGroup;

function isNavGroup(item: NavItem): item is NavItemGroup {
  return 'children' in item && Array.isArray((item as NavItemGroup).children);
}

const linkSx = {
  borderRadius: 2,
  mx: 1,
  mb: 0.5,
  '&.active': {
    bgcolor: 'primary.main',
    color: 'white',
    '& .MuiListItemIcon-root': { color: 'white' },
  },
};

const childLinkSx = {
  ...linkSx,
  pl: 5,
  py: 0.75,
  borderLeft: '2px solid',
  borderColor: 'divider',
  ml: 2,
  mr: 1,
  mb: 0.5,
};

export function AppNavDrawer() {
  const { user } = useAuth();
  const {
    hasFinanceAlerts,
    hasPresence,
    hasDeliveryNotes,
    hasSmartMatching: hasDeliveryNoteSmartMatching,
    hasIntegrations,
    hasPlanning,
    hasChecklist,
  } = useFeatureFlags();
  const { unreadCount } = useNotifications();
  const location = useLocation();

  const isOperatore = user?.role === 'OPERATORE';

  const presenzePathActive = location.pathname.startsWith('/app/attendance');
  const economicsPathActive =
    location.pathname.startsWith('/app/finance') ||
    location.pathname.startsWith('/app/analytics') ||
    location.pathname.startsWith('/app/suppliers');
  const operationsPathActive =
    location.pathname.startsWith('/app/worksites') ||
    location.pathname.startsWith('/app/reports') ||
    location.pathname.startsWith('/app/documents');
  const customerRelationsPathActive =
    location.pathname.startsWith('/app/customers') ||
    location.pathname.startsWith('/app/reminders');
  const resourcesPathActive =
    location.pathname.startsWith('/app/materials') ||
    location.pathname.startsWith('/app/equipment') ||
    location.pathname.startsWith('/app/supplier-material-mappings') ||
    location.pathname.startsWith('/app/config/warehouse');

  const [presenzeOpen, setPresenzeOpen] = useState(presenzePathActive);
  const [economicsOpen, setEconomicsOpen] = useState(economicsPathActive);
  const [operationsOpen, setOperationsOpen] = useState(operationsPathActive);
  const [customerRelationsOpen, setCustomerRelationsOpen] = useState(customerRelationsPathActive);
  const [resourcesOpen, setResourcesOpen] = useState(resourcesPathActive);

  useEffect(() => { if (presenzePathActive) setPresenzeOpen(true); }, [presenzePathActive]);
  useEffect(() => { if (economicsPathActive) setEconomicsOpen(true); }, [economicsPathActive]);
  useEffect(() => { if (operationsPathActive) setOperationsOpen(true); }, [operationsPathActive]);
  useEffect(() => { if (customerRelationsPathActive) setCustomerRelationsOpen(true); }, [customerRelationsPathActive]);
  useEffect(() => { if (resourcesPathActive) setResourcesOpen(true); }, [resourcesPathActive]);

  const presenzeChildIcons: Record<string, React.ReactNode> = {
    '/app/attendance': <AccessTimeIcon fontSize="small" />,
    '/app/attendance/worksite': <WorkIcon fontSize="small" />,
  };
  const resourcesChildIcons: Record<string, React.ReactNode> = {
    '/app/equipment': <BuildIcon fontSize="small" />,
    '/app/materials': <Inventory2Icon fontSize="small" />,
    '/app/materials/drafts': <Inventory2Icon fontSize="small" />,
    '/app/supplier-material-mappings': <Inventory2Icon fontSize="small" />,
    '/app/config/warehouse': <SettingsIcon fontSize="small" />,
  };
  const economicsChildIcons: Record<string, React.ReactNode> = {
    '/app/finance/dashboard': <AssessmentIcon fontSize="small" />,
    '/app/finance/user-costs': <EuroIcon fontSize="small" />,
    '/app/finance/alerts': <WarningAmberIcon fontSize="small" />,
    '/app/analytics': <AssessmentIcon fontSize="small" />,
    '/app/suppliers': <StorefrontIcon fontSize="small" />,
    '/app/analytics/procurement': <LocalShippingIcon fontSize="small" />,
  };
  const operationsChildIcons: Record<string, React.ReactNode> = {
    '/app/worksites': <WorkIcon fontSize="small" />,
    '/app/reports': <AssignmentIcon fontSize="small" />,
    '/app/documents': <DescriptionIcon fontSize="small" />,
    '/app/integrations': <SyncAltIcon fontSize="small" />,
    '/app/planning': <CalendarMonthIcon fontSize="small" />,
    '/app/checklists/templates': <ListAltIcon fontSize="small" />,
  };
  const customerRelationsChildIcons: Record<string, React.ReactNode> = {
    '/app/customers': <PersonIcon fontSize="small" />,
    '/app/reminders/templates': <CampaignIcon fontSize="small" />,
  };

  const navItems: NavItem[] = isOperatore
    ? [
        { label: 'Commesse', icon: <WorkIcon />, path: '/app/worksites' },
        ...(hasPlanning ? [{ label: 'Planning', icon: <CalendarMonthIcon />, path: '/app/planning' }] : []),
        {
          label: 'Notifiche',
          icon: (
            <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          ),
          path: '/app/notifications',
        },
      ]
    : [
        { label: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
        ...(['AMMINISTRATORE_AZIENDA', 'RESPONSABILE'].includes(user?.role ?? '') &&
        (hasFinanceAlerts || hasDeliveryNotes)
          ? [
              {
                label: 'Economics',
                icon: <AssessmentIcon />,
                children: [
                  ...(hasFinanceAlerts
                    ? [
                        { label: 'Dashboard economica', path: '/app/finance/dashboard' },
                        { label: 'Costo orario', path: '/app/finance/user-costs' },
                        { label: 'Alert finanziari', path: '/app/finance/alerts' },
                        { label: 'Analytics', path: '/app/analytics' },
                      ]
                    : []),
                  ...(hasDeliveryNotes
                    ? [
                        { label: 'Fornitori', path: '/app/suppliers' },
                        { label: 'Acquisti / Procurement', path: '/app/analytics/procurement' },
                      ]
                    : []),
                ],
              },
            ]
          : []),
        {
          label: 'Operativita',
          icon: <WorkIcon />,
          children: [
            { label: 'Commesse', path: '/app/worksites' },
            { label: 'Rapportini', path: '/app/reports' },
            { label: 'Documenti', path: '/app/documents' },
            ...(hasIntegrations ? [{ label: 'Integrazioni', path: '/app/integrations' }] : []),
            ...(hasPlanning ? [{ label: 'Planning', path: '/app/planning' }] : []),
            ...(hasChecklist ? [{ label: 'Checklist', path: '/app/checklists/templates' }] : []),
          ],
        },
        ...(hasPresence
          ? [
              {
                label: 'Presenze',
                icon: <ScheduleIcon />,
                children: [
                  { label: 'Presenze timbratura', path: '/app/attendance' },
                  { label: 'Presenze per commessa', path: '/app/attendance/worksite' },
                ],
              },
            ]
          : [{ label: 'Presenze da rapportino', icon: <ScheduleIcon />, path: '/app/attendance/worksite' }]),
        {
          label: 'Relazioni Cliente',
          icon: <PersonIcon />,
          children: [
            { label: 'Clienti', path: '/app/customers' },
            { label: 'Promemoria cliente', path: '/app/reminders/templates' },
          ],
        },
        {
          label: 'Notifiche',
          icon: (
            <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          ),
          path: '/app/notifications',
        },
        ...(user?.role === 'AMMINISTRATORE_AZIENDA'
          ? [{ label: 'Utenti', icon: <PeopleIcon />, path: '/app/users' }]
          : []),
        ...(user?.role === 'OPERATORE'
          ? [{ label: 'Materiali', icon: <Inventory2Icon />, path: '/app/materials' }]
          : ['AMMINISTRATORE_AZIENDA', 'RESPONSABILE'].includes(user?.role ?? '')
            ? [
                {
                  label: 'Risorse e Magazzino',
                  icon: <Inventory2Icon />,
                  children: [
                    { label: 'Attrezzature', path: '/app/equipment' },
                    { label: 'Materiali', path: '/app/materials' },
                    { label: 'Materiali da revisionare', path: '/app/materials/drafts' },
                    ...(hasDeliveryNoteSmartMatching
                      ? [{ label: 'Mapping fornitore–materiale', path: '/app/supplier-material-mappings' }]
                      : []),
                    { label: 'Configurazione magazzino', path: '/app/config/warehouse' },
                  ],
                },
                { label: 'Registro audit', icon: <HistoryIcon />, path: '/app/audit' },
              ]
            : []),
      ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h6" fontWeight={800} color="primary">
          Cantir
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => {
          if (isNavGroup(item)) {
            const key = `group-${item.label}`;
            const isOpen =
              item.label === 'Presenze'
                ? presenzeOpen
                : item.label === 'Economics'
                  ? economicsOpen
                  : item.label === 'Operativita'
                    ? operationsOpen
                    : item.label === 'Relazioni Cliente'
                      ? customerRelationsOpen
                      : item.label === 'Risorse e Magazzino'
                        ? resourcesOpen
                        : false;
            const setOpen =
              item.label === 'Presenze'
                ? setPresenzeOpen
                : item.label === 'Economics'
                  ? setEconomicsOpen
                  : item.label === 'Operativita'
                    ? setOperationsOpen
                    : item.label === 'Relazioni Cliente'
                      ? setCustomerRelationsOpen
                      : item.label === 'Risorse e Magazzino'
                        ? setResourcesOpen
                        : undefined;
            const childIcons =
              item.label === 'Presenze'
                ? presenzeChildIcons
                : item.label === 'Economics'
                  ? economicsChildIcons
                  : item.label === 'Operativita'
                    ? operationsChildIcons
                    : item.label === 'Relazioni Cliente'
                      ? customerRelationsChildIcons
                      : item.label === 'Risorse e Magazzino'
                        ? resourcesChildIcons
                        : {};
            const childEnd =
              item.label === 'Risorse e Magazzino'
                ? true
                : (path: string) => path === '/app/attendance';
            return (
              <Box
                key={key}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  bgcolor: isOpen ? 'action.hover' : 'transparent',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <ListItemButton
                  onClick={() => setOpen?.((prev) => !prev)}
                  sx={{
                    borderRadius: 2,
                    '&:hover': { bgcolor: isOpen ? 'action.selected' : 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  />
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pt: 0, pb: 0.5 }}>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.path}
                        component={NavLink}
                        to={child.path}
                        end={typeof childEnd === 'boolean' ? childEnd : childEnd(child.path)}
                        sx={childLinkSx}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {childIcons[child.path]}
                        </ListItemIcon>
                        <ListItemText primary={child.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }
          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              sx={linkSx}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
